import { ArrowLeft, MapPin, Clock, Star, Heart, MessageCircle, Share2, Briefcase, User, Verified, Award, Calendar, Euro, Building, Users, TrendingUp, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import BottomNavigation from "@/components/BottomNavigation";

const JobDetail = () => {
  const [responseMessage, setResponseMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasResponded, setHasResponded] = useState(false);
  const navigate = useNavigate();

  // Mock job data - would come from props/API in real app
  const job = {
    id: "1",
    title: "Mindfulness & Wellness Coach",
    industry: "Coaching & Wellness",
    timeType: "Part-time",
    location: {
      type: "Hybrid", // Online, Physical, Hybrid
      details: "Amsterdam, Nederland + Remote"
    },
    compensation: {
      amount: "€35-45",
      period: "per uur",
      benefits: ["Flexibele werktijden", "Professionele ontwikkeling", "Zorgverzekering"]
    },
    company: {
      name: "Conscious Living Collective",
      avatar: "https://images.pexels.com/photos/3184325/pexels-photo-3184325.jpeg?auto=compress&cs=tinysrgb&w=200",
      verified: true,
      description: "Een community-gerichte wellness center gericht op holistische healing en persoonlijke groei.",
      rating: 4.8,
      employees: "15-50"
    },
    postedDate: "2 dagen geleden",
    applicants: 12,
    deadline: "15 januari 2025",
    description: `We zoeken een gepassioneerde en ervaren Mindfulness & Wellness Coach om deel uit te maken van onze bewuste community. Dit is een perfecte kans voor iemand die een betekenisvolle impact wil maken terwijl je flexibel werkt.

**Wat je gaat doen:**
• Een-op-een coaching sessies geven met cliënten
• Groeps mindfulness workshops en meditatie sessies leiden  
• Gepersonaliseerde wellness plannen ontwikkelen voor individuele cliënten
• Samenwerken met ons holistische gezondheids team
• Cliënt voortgang documentatie bijhouden

**Wat we zoeken:**
• Gecertificeerd mindfulness of wellness coach (2+ jaar ervaring)
• Sterke communicatie en empathie vaardigheden
• Ervaring met meditatie en stress management technieken
• Gepassioneerd over bewust leven en persoonlijke groei
• Beschikbaar voor avond en weekend sessies

**Waarom bij ons werken:**
• Werken met gelijkgestemde bewuste individuen
• Flexibele planning die bij jouw levensstijl past
• Mogelijkheid om je praktijk te groeien in een ondersteunende omgeving
• Toegang tot voortdurende educatie en workshops
• Collaboratieve en voedende werkcultuur

Deze rol is perfect voor iemand die werk-leven balans waardeert en anderen wil helpen op hun wellness journey.`,
    requirements: [
      "Gecertificeerd mindfulness of wellness coach",
      "2+ jaar coaching ervaring",
      "Sterke interpersoonlijke vaardigheden",
      "Flexibele schema beschikbaarheid",
      "Passie voor holistische wellness"
    ]
  };

  const handleRespondToJob = async () => {
    if (!responseMessage.trim()) {
      alert("Schrijf eerst een bericht voordat je reageert");
      return;
    }

    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      console.log("Job response submitted:", {
        jobId: job.id,
        message: responseMessage
      });
      setIsSubmitting(false);
      setHasResponded(true);
    }, 1500);
  };

  const handleGoBack = () => {
    console.log("Going back to previous page");
    navigate(-1);
  };

  const getLocationIcon = () => {
    switch (job.location.type) {
      case "Online":
        return "🌐";
      case "Physical":
        return "🏢";
      case "Hybrid":
        return "🔄";
      default:
        return "📍";
    }
  };

  // Success screen after responding
  if (hasResponded) {
    return (
      <div className="min-h-screen bg-elegant">
        <div className="flex items-center justify-center min-h-screen p-6">
          <Card className="card-premium w-full max-w-md">
            <CardContent className="p-8 text-center">
              <div className="mb-6">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-green-100">
                  <Send className="h-8 w-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-serif text-navy-800 mb-2">
                  Reactie Verzonden!
                </h2>
                <p className="text-navy-700">
                  Je sollicitatie is succesvol ingediend. De werkgever zal je reactie bekijken en spoedig contact met je opnemen.
                </p>
              </div>

              <div className="bg-cream-50 rounded-xl p-4 mb-6 text-left border border-cream-200">
                <h3 className="font-serif font-semibold mb-2 text-navy-800">Wat gebeurt er nu:</h3>
                <div className="space-y-1 text-sm text-navy-700">
                  <p>• De werkgever bekijkt je sollicitatie</p>
                  <p>• Je ontvangt een notificatie als ze geïnteresseerd zijn</p>
                  <p>• Controleer je berichten voor directe communicatie</p>
                </div>
              </div>

              <Button 
                onClick={handleGoBack}
                className="btn-elegant-primary w-full"
              >
                Meer Vacatures Bekijken
              </Button>
            </CardContent>
          </Card>
        </div>
        <BottomNavigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-elegant">
      {/* Header */}
      <div className="sticky top-0 z-10 glass-effect border-b border-cream-300">
        <div className="px-6 py-4">
          <div className="flex items-center gap-4 mb-4">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleGoBack}
              className="btn-elegant-secondary"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              <span>Terug</span>
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-serif text-navy-800">Vacature Details</h1>
              <p className="text-sm text-navy-600">
                Ontdek kansen bij women on a mission
              </p>
            </div>
          </div>

          {/* Job Header */}
          <div className="card-premium p-4 mb-4">
            <div className="text-center">
              <div className="mb-2">
                <h1 className="script-text text-3xl text-navy-800">
                  Vacature Details
                </h1>
              </div>
              <p className="text-sm text-navy-600">
                Vind je perfecte match in onze community
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-6 pb-32">
        {/* Company Info Card */}
        <Card className="card-premium mb-6">
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-4">
              <Avatar className="h-16 w-16">
                <AvatarImage src={job.company.avatar} alt={job.company.name} />
                <AvatarFallback className="bg-cream-200 text-navy-700 text-lg">
                  {job.company.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-serif font-semibold text-navy-800">{job.company.name}</h3>
                  {job.company.verified && (
                    <Award className="h-4 w-4 text-gold-500" />
                  )}
                </div>
                <p className="text-sm text-navy-700 mb-1">{job.company.description}</p>
                <div className="flex items-center gap-4 text-xs text-navy-600">
                  <span>⭐ {job.company.rating} bedrijf rating</span>
                  <span>• {job.company.employees} werknemers</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4 text-sm text-navy-600">
              <span>Geplaatst {job.postedDate}</span>
              <span>•</span>
              <span>{job.applicants} sollicitanten</span>
              <span>•</span>
              <span>Deadline: {job.deadline}</span>
            </div>
          </CardContent>
        </Card>

        {/* Job Details Card */}
        <Card className="card-premium mb-6">
          <CardContent className="p-6">
            {/* Job Title */}
            <h2 className="text-3xl font-serif text-navy-800 mb-4">
              {job.title}
            </h2>

            {/* Job Meta Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Industry & Time Type */}
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-navy-600" />
                <span className="text-navy-700">
                  {job.timeType} – {job.industry}
                </span>
              </div>

              {/* Location */}
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-navy-600" />
                <span className="text-navy-700">
                  {getLocationIcon()} {job.location.details}
                </span>
              </div>
            </div>

            {/* Compensation */}
            <div className="bg-cream-50 rounded-xl p-4 mb-6 border border-cream-200">
              <div className="flex items-center gap-2 mb-2">
                <Euro className="h-5 w-5 text-navy-600" />
                <span className="font-semibold text-navy-800">Salaris & Voordelen</span>
              </div>
              <p className="text-2xl font-bold mb-2 text-navy-800">
                {job.compensation.amount} {job.compensation.period}
              </p>
              <div className="flex flex-wrap gap-2">
                {job.compensation.benefits.map((benefit, index) => (
                  <Badge key={index} variant="secondary" className="bg-cream-100 text-navy-700 text-xs">
                    {benefit}
                  </Badge>
                ))}
              </div>
            </div>

            <Separator className="my-6 bg-cream-300" />

            {/* Full Description */}
            <div className="mb-6">
              <h4 className="font-serif font-semibold mb-4 flex items-center gap-2 text-navy-800">
                <Briefcase className="h-5 w-5" />
                Vacature Beschrijving
              </h4>
              <div className="prose prose-sage max-w-none">
                {job.description.split('\n').map((paragraph, index) => {
                  if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                    return (
                      <h5 key={index} className="font-serif font-semibold mt-4 mb-2 text-navy-800">
                        {paragraph.replace(/\*\*/g, '')}
                      </h5>
                    );
                  } else if (paragraph.startsWith('•')) {
                    return (
                      <p key={index} className="ml-4 mb-1 text-navy-700">
                        {paragraph}
                      </p>
                    );
                  } else if (paragraph.trim()) {
                    return (
                      <p key={index} className="mb-3 leading-relaxed text-navy-700">
                        {paragraph}
                      </p>
                    );
                  }
                  return null;
                })}
              </div>
            </div>

            {/* Requirements */}
            <div>
              <h4 className="font-serif font-semibold mb-3 text-navy-800">Belangrijkste Vereisten:</h4>
              <div className="space-y-2">
                {job.requirements.map((requirement, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 bg-gold-500" />
                    <span className="text-sm text-navy-700">{requirement}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Woman on a Mission Badge */}
            <div className="bg-gold-50 rounded-lg p-4 mt-6 border border-gold-200">
              <p className="font-medium text-sm flex items-center gap-2 text-navy-800">
                <div className="gradient-gold text-white px-3 py-1 rounded-full text-xs font-bold">
                  WOM
                </div>
                Woman on a Mission - Geverifieerde Werkgever
                <Users className="h-4 w-4 text-gold-600" />
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Response Form */}
        <Card className="card-premium mb-6">
          <CardHeader>
            <CardTitle className="text-xl font-serif flex items-center gap-2 text-navy-800">
              <MessageSquare className="h-5 w-5" />
              Reageer op deze vacature
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="response" className="font-medium text-navy-700">
                Jouw bericht aan de werkgever
              </Label>
              <Textarea 
                id="response"
                placeholder="Vertel waarom je geïnteresseerd bent in deze rol en wat jou een geweldige kandidaat maakt..."
                value={responseMessage}
                onChange={(e) => setResponseMessage(e.target.value)}
                className="min-h-[120px] border-cream-300 focus:border-gold-400 focus:ring-gold-400"
              />
              <p className="text-xs text-navy-600">
                Vermeld je relevante ervaring, beschikbaarheid en waarom je gepassioneerd bent over deze kans.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Fixed Bottom Response Section */}
      <div className="fixed bottom-0 left-0 right-0 glass-effect border-t border-cream-300 p-6 z-50">
        <div className="max-w-md mx-auto">
          <Button 
            onClick={handleRespondToJob}
            disabled={!responseMessage.trim() || isSubmitting}
            className="btn-elegant-primary w-full py-4 text-lg font-semibold rounded-xl shadow-elegant-lg"
            size="lg"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Reactie versturen...
              </>
            ) : (
              <>
                <Send className="h-5 w-5 mr-2" />
                Reageer op deze vacature
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNavigation />
    </div>
  );
};

export default JobDetail;